import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { FormattedMessage, injectIntl } from 'react-intl';
import { colorToArray, i18nIntl, isColorBright, isDesktop, isIOS, isNotEmpty, RGB_2_HEX } from '../../lib/ep';
import { useCSS } from '../../lib/hooks';

import ActionButton, { SquaredButton } from '../builder/ActionButton';
import ButtonCollection from '../builder/ButtonCollection';
import ActionLink from '../builder/ActionLink';

const isInStandaloneMode = () => {
	return (window.matchMedia('(display-mode:standalone)').matches || window.navigator.standalone) ? true : false;
};

const SVGWrapper = ({ w, h, factor, ...props }) => {
	return (
		<svg
			version="1.1"
			width={w * factor}
			height={h * factor}
			viewBox={`0 0 ${w} ${h}`}
			preserveAspectRatio="xMidYMid meet"
			class="default_svg"
			style={{ overflow:'visible', margin:'0 0.2em' }}
		>
			{props.children}
		</svg>
	)
};

const IconSettingsIOS = ({ color, ...props }) => {
	const w = 24;
	const h = 30;
	const factor = props?.factor ?? 1;

	const default_props = {
		fill:'none',
		stroke:color,
		strokeWidth:`${1 / factor}px`,
		strokeLinecap:'round',
	};

	const arrow_props = {
		...default_props,
		strokeLinejoin:'round',
	};

	return (
		<SVGWrapper w={w} h={h} factor={factor}>
			<polyline
				style={arrow_props}
				points="12.5,19.5 12.5,0"
			/>

			<polyline
				style={arrow_props}
				points="17.5,5 12.5,0 7.5,5"
			/>

			<path
				style={default_props}
				d="M8,9H3.5C1.57,9,0,10.57,0,12.5v14C0,28.43,1.57,30,3.5,30h18c1.93,0,3.5-1.57,3.5-3.5v-14c0-1.93-1.57-3.5-3.5-3.5H17"
			/>
		</SVGWrapper>
	)
};

const IconOptionIOS = ({ color, ...props }) => {
	const w = 30;
	const h = 30;
	const factor = props?.factor ?? 1;

	const default_props = {
		fill:'none',
		stroke:color,
		strokeWidth:`${1 / factor}px`,
		strokeLinecap:'round',
	};

	const arrow_props = {
		...default_props,
		strokeLinejoin:'round',
	};

	return (
		<SVGWrapper w={w} h={h} factor={factor}>
			<polyline
				style={arrow_props}
				points="15,7 15,23"
			/>

			<polyline
				style={arrow_props}
				points="23,15 7,15"
			/>

			<path
				style={default_props}
				d="M3.5,0C1.57,0,0,1.57,0,3.5v23C0,28.43,1.57,30,3.5,30h23c1.93,0,3.5-1.57,3.5-3.5v-23C30,1.57,28.43,0,26.5,0H3.5z"
			/>
		</SVGWrapper>
	)
};

const IconSettingsAndroid = ({ color, ...props }) => {
	const w = 6;
	const h = 30;
	const factor = props?.factor ?? 1;

	const default_props = {
		fill:color,
		stroke:color,
		strokeWidth:`${1 / factor}px`,
	};

	return (
		<SVGWrapper w={w} h={h} factor={factor}>
			<circle
				style={default_props}
				cx="3"
				cy="3"
				r="3"
			/>

			<circle
				style={default_props}
				cx="3"
				cy="15"
				r="3"
			/>

			<circle
				style={default_props}
				cx="3"
				cy="27"
				r="3"
			/>
		</SVGWrapper>
	)
};

const IconOptionAndroid = ({ color, ...props }) => {
	const w = 21;
	const h = 30;
	const factor = props?.factor ?? 1;

	const default_props = {
		fill:color,
		stroke:color,
		strokeWidth:`${1 / factor}px`,
	};

	const arrow_props = {
		fill:'transparent',
		stroke:color,
		strokeWidth:`${1.3 / factor}px`,
	};

	return (
		<SVGWrapper w={w} h={h} factor={factor}>
			<polyline
				style={arrow_props}
				points="2.5,10 10,10 10,17.5"
			/>

			<polyline
				style={arrow_props}
				points="10,10 0,20"
			/>

			<path
				style={default_props}
				d="M18.59,0H5.41C4.08,0,3,1.08,3,2.41V6h3V4h12v22H6v-2H3v3.59C3,28.92,4.08,30,5.41,30h13.18c1.33,0,2.41-1.08,2.41-2.41V2.41C21,1.08,19.92,0,18.59,0z"
			/>
		</SVGWrapper>
	)
};

const PWAIcon = (props) => {
	const url = (isNotEmpty(props, 'url')) ? props.url : 'https://static.cstmapp.com/img/wpa/logo192x192.png';

	return (
		<img
			class="pwa_icon"
			src={url}
			width="100%"
			height="100%"
			alt="PWA icon"
		/>
	)
};

export const PWAInstallBannerBuilder = (props) => {
	useCSS('comp/pwa_layer.css');

	return (
		<div class="pwa_device_preview">
			<div id="pwa">
				<PWAInstallBanner
					{...props}
					force_display={true}
					is_builder={true}
				/>
			</div>
		</div>
	)
};

export const HeaderPWABanner = (props) => {
	useCSS('comp/pwa_layer.css');

	const promotion = props?.promotion?.Promotion ?? false;
	const is_builder = props?.is_builder ?? false;
	const is_desktop = (isDesktop()) ? true : false;

	const actual_page = props?.actual_page ?? 'landing';

	//const auto_display_pages = ['landing', 'promoview', 'login'];
	const auto_display_pages = ['success', 'already', 'my_participation'];

	const [ is_standalone, setIsStandalone ] = useState(isInStandaloneMode());
	const [ display, setDisplay ] = useState(false);
	const [ auto_display, setAutoDisplay ] = useState((auto_display_pages.includes(actual_page)) ? true : false);

	const handleAppInstalled = () => {
		setIsStandalone(true);
	};

	useEffect(() => {
		if(is_builder) return;

		window.addEventListener('appinstalled', handleAppInstalled);

		return () => {
			window.removeEventListener('appinstalled', handleAppInstalled);
		};
	}, []);

	if(is_builder) return null;	// we don't display the link in header in builder

	if (window?.features?.includes('push_notifications') && is_standalone) {
		return (
			<ActionLink onClick={() => {
				window.askPermission();
			}}>🔔 Enable</ActionLink>
		)
	}

	if(is_desktop || is_standalone) return null;

	return (
		<>
			<div class="pwa_header" onClick={() => setDisplay(true)}>
				<PWAIcon
					url={promotion.full_manifest_img}
				/>
				Install
			</div>

			{window?.features?.includes('push_notifications') && (
			<ActionLink onClick={() => {
				if (!('Notification' in window)) {
					return;
				}
				Notification.requestPermission();
			}}>🔔 Enable</ActionLink>
			)}

			{(display || auto_display) && (
				<PWAInstallBannerPublic
					{...props}
					force_display={(display === true) ? true : false}
					auto_display={auto_display}
					onClose={() => {
						setDisplay(false);
						setAutoDisplay(false);
					}}
				/>
			)}
		</>
	)
};

const PWAInstallBannerPublic = (props) => {
	let element = document.getElementById('pwa');
	if(element == null) {
		element = document.createElement('div');
		element.id = 'pwa';
		document.body.appendChild(element);
		element = document.getElementById('pwa');
	}

	return ReactDOM.createPortal(
		<PWAInstallBanner
			{...props}
		/>,
		element,
	);
};

const PWAInstallBanner = (props) => {
	useCSS('comp/pwa_layer.css');

	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const is_builder = props?.is_builder ?? false;
	const force_display = props?.force_display ?? false;
	const auto_display = props?.auto_display ?? false;
	const device = (isIOS()) ? 'ios' : 'android';
	const is_desktop = (isDesktop()) ? true : false;

	const promotion = props?.promotion?.Promotion ?? false;
	const manifest_enabled = promotion?.manifest_enabled ?? false;
	const name_cookie = `manifest_${promotion.id}`;
	const has_cookie_manifest = getCookie(name_cookie);

	const [ deferred_prompt, setDeferredPrompt ] = useState(false);
	const [ is_standalone, setIsStandalone ] = useState(isInStandaloneMode());
	const [ display, setDisplay ] = useState((is_builder) ? true : false);

	const closeLayer = (set_cookie = true) => {
		if(is_builder) return;

		setDisplay(false);
		if(props.onClose != undefined) props.onClose();
		if(set_cookie) setCookieMinutes(name_cookie, 1, 15, window.domain_url);
	};

	const closeLayerFromOutside = (e) => {
		if(e.target != e.currentTarget) return;

		closeLayer();
	};

	const install = () => {
		if(is_builder) return;
		if(deferred_prompt === false) return;

		deferred_prompt.prompt();
		deferred_prompt.userChoice.then((choiceResult) => {
			if(choiceResult.outcome !== 'accepted') {
				// the user clicked the "install" button but then canceled the prompt:
				closeLayer();
				return;
			}

			closeLayer(false);
		});
	};

	const handleBeforeInstallPrompt = (e) => {
		e.preventDefault();
		setDeferredPrompt(e);
	};

	const handleAppInstalled = () => {
		setDeferredPrompt(false);
		setIsStandalone(true);
	};

	useEffect(() => {
		if(is_builder) return;

		window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
		window.addEventListener('appinstalled', handleAppInstalled);

		if(auto_display) {
			setTimeout(() => {
				setDisplay(true);
			}, 2000);
		} else if(force_display) {
			setDisplay(true);
		}

		return () => {
			window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
			window.removeEventListener('appinstalled', handleAppInstalled);
		};
	}, []);

	if(promotion === false) return null;
	if(manifest_enabled === false) return null;
	if(is_builder === false && (is_desktop || is_standalone || (has_cookie_manifest && force_display === false))) return null;

	const title_placeholder = `Easypromos ${i18n("Web App")} ${promotion.id}`;

	const classes = ['pwa_install_banner'];
	if(display) classes.push('is_visible');

	const icon_default = {
		factor:0.5,
		color:'rgba(0, 0, 0, 1)'
	};

	const IconSettings = (device == 'ios') ? IconSettingsIOS : IconSettingsAndroid;
	const IconOption = (device == 'ios') ? IconOptionIOS : IconOptionAndroid;

	const main_color = promotion?.manifest_theme_color ?? (promotion?.layout_props?.color?.main_color ?? 'rgba(0, 0, 0, 1)');
	const is_main_color_bright = isColorBright(RGB_2_HEX(main_color));

	return (
		<aside
			class={classes.join(" ")}
			onClick={closeLayerFromOutside}
			style={{
				'--btn-text-color':(is_main_color_bright) ? '0, 0, 0' : '255, 255, 255',
				'--main-color':colorToArray(main_color),
			}}
		>
			<div class="c">
				<ButtonCollection
					_class={{ other:'close_btn' }}
				>
					<SquaredButton
						onClick={closeLayer}
						icon='knk-off transparentButton'
						_class={{ other:'not_bordered_button' }}
						color='darkgray'
					/>
				</ButtonCollection>

				<div class="main_info">
					<PWAIcon
						url={promotion.full_manifest_img}
					/>

					<h2>{isNotEmpty(promotion, 'manifest_name') ? promotion.manifest_name : title_placeholder}</h2>
				</div>

				<p>{i18n("Add this app to your homescreen for easy access and better experience.")}</p>

				{((is_builder || deferred_prompt === false) ? true : false) ? (
					<p>
						<FormattedMessage
							id="Tap {icon} and then ''{option} {label}''"
							values={{
								icon:<IconSettings {...icon_default} />,
								option:<IconOption {...icon_default} />,
								label:<strong>{i18n("Add to Home Screen")}</strong>,
							}}
						/>
					</p>
				) : (
					<ButtonCollection
						_class={{ other:'install_btn' }}
					>
						<ActionButton
							_fullclass=""
							onClick={install}
						>Install</ActionButton>
					</ButtonCollection>
				)}
			</div>
		</aside>
	)
};

export default injectIntl(PWAInstallBanner);